import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Loader2, Save, Sparkles, Wand2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/apiBase';

/**
 * Create/Edit a changelog entry.
 * Props:
 *  - open, onOpenChange
 *  - entry (nullable) — if provided, edit mode; else create mode
 *  - workspaceId
 *  - onSaved(newEntry)
 *  - draftFromSignalId (optional) — if provided, adds an "AI draft" button
 */
const ChangelogEditorDrawer = ({ open, onOpenChange, entry, workspaceId, onSaved, draftFromSignalId }) => {
  const isEdit = !!entry?.id;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(entry?.title || '');
      setDescription(entry?.description || '');
    }
  }, [open, entry]);

  const handleAiDraft = async () => {
    if (!draftFromSignalId) return;
    setDrafting(true);
    try {
      const res = await axios.post(`${API_URL}/api/changelog/draft/${draftFromSignalId}`);
      setTitle(res.data.title || '');
      setDescription(res.data.description || '');
      toast.success('AI draft ready — review before publishing');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI draft failed');
    } finally {
      setDrafting(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const res = await axios.put(`${API_URL}/api/changelog/${entry.id}`, {
          title: title.trim(),
          description: description.trim(),
          workspace_id: workspaceId,
        });
        toast.success('Entry updated');
        onSaved?.(res.data.entry);
      } else {
        const res = await axios.post(`${API_URL}/api/changelog`, {
          workspace_id: workspaceId,
          title: title.trim(),
          description: description.trim(),
        });
        toast.success('Entry published to changelog');
        onSaved?.(res.data.entry);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="changelog-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {isEdit ? 'Edit changelog entry' : 'New changelog entry'}
          </DialogTitle>
          <DialogDescription>
            Write a short, honest post about what changed. Users on your public changelog will see this.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {draftFromSignalId && !isEdit && (
            <Button
              variant="outline"
              onClick={handleAiDraft}
              disabled={drafting}
              className="w-full"
              data-testid="changelog-ai-draft"
            >
              {drafting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Drafting…</> : <><Wand2 className="h-4 w-4 mr-2" />AI-draft this from the decision</>}
            </Button>
          )}

          <div className="space-y-2">
            <Label htmlFor="cl-title">Title</Label>
            <Input
              id="cl-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Slack integration is live"
              data-testid="changelog-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cl-body">Body</Label>
            <Textarea
              id="cl-body"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={7}
              maxLength={5000}
              placeholder="You can now…"
              data-testid="changelog-body-input"
            />
            <p className="text-xs text-muted-foreground">{description.length}/5000 characters</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !title.trim()} className="btn-cta" data-testid="changelog-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : <><Save className="h-4 w-4 mr-2" />{isEdit ? 'Save changes' : 'Publish entry'}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangelogEditorDrawer;
